/**
 * The traversal class.
 * Handles functionality for the Supernav's traversable menu.
 * 
 * TODO:
 * - Eventually, we will want to sever our dependence on jQuery.
 * - When adding Bellco into the fold, we'll want to pull animation info from CSS vars rather than hard-coded.
 */

/* Dependencies --------------------------------------------- */

// External

import { tabbable } from 'tabbable'

// Internal

import {
	closeEventName as burgerhoodCloseEventName
} from '../burgerhood'

import {
	closeEventName as dropdownCloseEventName,
	escapeEventName as dropdownEscapeEventName,
	openEventName as dropdownOpenEventName,
	openCompleteEventName as dropdownOpenCompleteEventName,
} from '../dropdown'

/* Main class ----------------------------------------------- */

function singleton( config ) {

    /** Setup --------------------------- */

    /**
     * The return object.
	 * 
     * Tracks stuff in the old-school way of doing class-like structures.
	 * Believe it or not, I didn't have time to re-factor things.
     */
    let object = { config: config }

    /** Events -------------------------- */

    /**
     * Wires up event listeners.
     */
    object.listen = function () {
		jQuery( object.config.eventDelegatorSelector ).on(
            burgerhoodCloseEventName,
            object.config.selector,
            object.handleBurgerhoodClose
        )
        jQuery( object.config.eventDelegatorSelector ).on(
            dropdownOpenEventName,
            object.config.dropdownSelector,
            object.handleDropdownOpen
        )
        jQuery( object.config.eventDelegatorSelector ).on(
            dropdownOpenCompleteEventName,
            object.config.dropdownSelector,
            object.handleDropdownOpenComplete
        )
        jQuery( object.config.eventDelegatorSelector ).on(
            dropdownCloseEventName,
            object.config.dropdownSelector,
            object.handleDropdownClose
        )
        jQuery( object.config.eventDelegatorSelector ).on(
            'click',
            object.config.linkSelector,
            object.handleLinkClick
        )
        jQuery( object.config.eventDelegatorSelector ).on(
            'click',
            object.config.backButtonLinkSelector,
            object.handleBackClick
        )
        jQuery( object.config.eventDelegatorSelector ).on(
            'click',
            object.config.breadcrumbAncestorLinkSelector,
            object.handleBreadcrumbAncestorClick
        )
        jQuery( object.config.eventDelegatorSelector ).on(
            'keydown',
            object.config.dropdownSelector,
            object.handleDropdownKeydown
        )
    }

    /** Handlers ------------------------ */

	/** Burgerhood ----------------- */

	/**
	 * Handles the hamburger nav closing.
	 * Resets the active layers in the tree.
	 */
	object.handleBurgerhoodClose = function( event ) {
		const nav = jQuery( event.currentTarget )
		object.traverse( nav )
	}

	/** Dropdown ------------------- */

    /**
     * Handles the dropdown opening.
     * Traverses down a layer to the relevant link's submenu.
     */
    object.handleDropdownOpen = function( event ) {

        const dropdown = jQuery( event.currentTarget )
        const link = dropdown.siblings( object.config.linkSelector )
        const item = link.closest( object.config.itemSelector )

        const siblingItems = item.siblings( object.config.itemSelector )
        const siblingLinks = siblingItems.children( object.config.linkSelector )

        // Toggle aria-expanded
        link.attr( 'aria-expanded', 'true' )

        // Remove active classes from all links and items except the active ones

        siblingItems.removeClass( object.config.isActiveClass )
        siblingLinks.removeClass( object.config.isActiveClass )

        // Then, only add the active class to the relevant links and items

        link.addClass( object.config.isActiveClass )
        item.addClass( object.config.isActiveClass )

        object.positionDropdown( dropdown )
    }

    /**
     * Handles the dropdown opening completely.
     * Focuses the first tabbable element in the dropdown if the interaction began with a click.
     */
    object.handleDropdownOpenComplete = function( event ) {

		if ( event.originalEvent.detail.openingEvent !== 'click' ) return

        const dropdown = jQuery( event.currentTarget )
        object.focusElement( jQuery( tabbable( dropdown.get( 0 ) ) ).first() )
    }

    /**
     * Handles closing the dropdown.
     * Resets the active layers in the tree.
     */
    object.handleDropdownClose = function( event ) {

        const dropdown = jQuery( event.currentTarget )
        const nav      = dropdown.closest( object.config.selector )
        const item     = dropdown.parent( object.config.itemSelector )
        const link     = dropdown.siblings( object.config.linkSelector )

        // Toggle aria-expanded
        link.attr( 'aria-expanded', 'false' )

        object.traverse( nav )

        item.removeClass( object.config.isActiveClass )
        link.removeClass( object.config.isActiveClass )

		if ( window.innerWidth < object.config.hamburgerBreakpoint ) return
        dropdown.css( 'left', 0 )
    }

	/**
     * Handles keydown on focusable elements within dropdowns.
     * Bridges several different functions that handle accessability.
     *
     * TODO: Integrate arrow navigation like traditional OS UI menus.
     */
    object.handleDropdownKeydown = function( event ) {

        switch ( event.key ) {
            case 'Tab':
                object.handleDropdownTab( event )
                break
            case 'Escape':
                object.handleDropdownEscape( event )
                break
        }
    }

    /**
     * Handles tabbing through elements within dropdowns.
     */
    object.handleDropdownTab = function( event ) {

        const element = jQuery( event.target )
        const layer = element.closest( object.config.layerSelector )

        // We need to find the scope of all elements the user would expect to tab to.
        // If we can't identify the layer or we're on the the content layer, we need to assume that the dropdown is that scope.
        // This is to include the menu content button into the tabbable scope.

        const tabbableScope =
            ! layer.length || layer.is( object.config.contentLayerSelector )
                ? element.closest( object.config.dropdownSelector )
                : layer;

        const tabbables = jQuery( tabbable( tabbableScope.get( 0 ) ) )

        const isFirst = element.is( tabbables.first() )
        const isLast = element.is( tabbables.last() )
        const isTabbingBackwards = event.shiftKey

        if ( isFirst && isTabbingBackwards ) {
            event.preventDefault();
            object.focusElement( tabbables.last() )
        }
		else if ( isLast && ! isTabbingBackwards ) {
            event.preventDefault();
            object.focusElement( tabbables.first() )
        }
    }

    /**
     * Handles escaping from the dropdown.
     */
    object.handleDropdownEscape = function( event ) {

        event.preventDefault()

        const dropdown = jQuery( event.currentTarget )
        const link = dropdown.siblings( object.config.linkSelector )
        const item = link.closest( object.config.itemSelector )

        object.traverse( dropdown.closest( object.config.selector ) )
        object.focusElement( link )
        item.get( 0 ).dispatchEvent( new CustomEvent( dropdownEscapeEventName, { bubbles: true } ) )
    }

	/** Traversal elements --------- */

    /**
     * Handles clicking links.
     * Traverses down a layer to the relevant link's submenu.
     */
    object.handleLinkClick = function( event ) {
        const link = jQuery( event.currentTarget )
        const item = link.closest( object.config.itemSelector )

        if ( item.hasClass( object.config.hasChildrenClass ) ) {
            const nav = item.closest( object.config.selector )
            event.preventDefault();

            let targetLayer = link.siblings( object.config.layerSelector )
            targetLayer = targetLayer.length
                ? targetLayer
                : link.siblings( object.config.dropdownSelector )
                    .children( object.config.layerSelector )

            const layerChain = object.traverse( nav, targetLayer )
            object.focusElement( jQuery( tabbable( layerChain.last().get( 0 ) ) ).first() )
        }
    }

    /**
     * Handles clicking the back button.
     *
     * @see object.up()
     */
    object.handleBackClick = function( event ) {

        const back = jQuery( event.currentTarget )
        const nav = back.closest( object.config.selector )

        const layerChain = object.up( nav )
        object.focusElement( layerChain.last().siblings( object.config.linkSelector ) )
    }

    /**
     * Handles clicking the breadcrumb ancestors.
     *
     * @see object.up()
     */
    object.handleBreadcrumbAncestorClick = function( event ) {

        const ancestor = jQuery( event.currentTarget )
        const nav = ancestor.closest( object.config.selector )

        const layerChain = object.up( nav, parseInt( ancestor.data( object.config.traversesAttribute ) || 1 ) )
        object.focusElement( layerChain.last().siblings( object.config.linkSelector ) )
    }

    /* Main methods --------------------- */

    /**
     * Traverses up a given number of layers from the active one.
     *
     * @return The layer chain trough which we traversed, if it exists, ending with the active layer.
     *
     * @see object.traverse()
     */
    object.up = function( nav, layers = 1 ) {

        nav = jQuery( nav )
        const activeLayer = object.getActiveLayer( nav )

		if ( ! activeLayer ) return false

        const targetLayer = activeLayer.parents( object.config.layerSelector ).eq( layers - 1 )

		return object
			.traverse( nav, targetLayer )
			.add( activeLayer )
    }

    /**
     * Traverses to a given layer. Any layer.
     * If false, resets the entire tree.
     *
     * @return The layer chain trough which we traversed, if it exists.
     */
    object.traverse = function( nav, targetLayer = false ) {

		// Interestingly, we have to reset everything before actually traversing.

		nav = jQuery( nav )
		const activeLayer = object.getActiveLayer( nav )

        const layers = nav.find( object.config.layerSelector )
        const items  = nav.find( object.config.itemSelector )
        const links  = nav.find( object.config.linkSelector )

        layers.removeClass( object.config.isActiveClass ).removeClass( object.config.hasActiveChildClass )

        items.removeClass( object.config.isActiveClass )
        links.removeClass( object.config.isActiveClass )

        // Reset aria-expanded on all links with children
        links.each( function( index, link ) {
            link = jQuery( link )
            const item = link.closest( object.config.itemSelector )
            if ( item.hasClass( object.config.hasChildrenClass ) ) {
                link.attr( 'aria-expanded', 'false' )
            }
        } )

        // Then, only turn things back on if there's a layer specified.
        // Goes up the chain of layers involved, adding relevant active classes along the way.
        // Focuses the first

        if ( targetLayer && targetLayer.length ) {

			if ( window.innerWidth < object.config.hamburgerBreakpoint ) {
				const menuWrapper = targetLayer.closest( object.config.menuWrapperSelector )
				menuWrapper.animate( { scrollTop: 0 }, object.config.animationDuration )
			}

            const layerChain = object.getLayerChain( targetLayer )

            layerChain.each( ( index, chainLayer ) => {
                chainLayer = jQuery( chainLayer )
                const link = chainLayer.siblings( object.config.linkSelector )
                const item = link.closest( object.config.itemSelector )

                chainLayer.addClass( object.config.isActiveClass )

                if ( index < layerChain.length - 1 ) {
                    chainLayer.addClass( object.config.hasActiveChildClass )
                }

                link.addClass( object.config.isActiveClass )
                item.addClass( object.config.isActiveClass )

                // Set aria-expanded for active links with children
                if ( item.hasClass( object.config.hasChildrenClass ) ) {
                    link.attr( 'aria-expanded', 'true' )
                }
            } )

			targetLayer.get( 0 ).dispatchEvent( new CustomEvent( traverseEventName ) )
            object.setLayerHeights( nav )
            return layerChain
        }

		// If not, then we assume we're traversing to the top and would need to close any dropdowns

		const closestDropdown = activeLayer ? activeLayer.closest( object.config.dropdownSelector ) : null

		if ( closestDropdown && closestDropdown.length ) {
			closestDropdown.get( 0 ).dispatchEvent( new CustomEvent( traverseEventName ) )
			object.setLayerHeights( nav, true )
			return closestDropdown
		}

		object.setLayerHeights( nav, true )
        return false
    }

    /** Secondary methods --------------- */

	/**
     * Checks if dropdown menu positioning exceeds the viewport.
     * Adjusts the dropdown positioning if necessary to ensure the entire dropdown remains visible for narrow screen sizes.
     */
    object.positionDropdown = function( element ) {

		if ( window.innerWidth < object.config.hamburgerBreakpoint ) return

        const offset = 40

        if ( element[0] == null || typeof element[0] == 'undefined' ) return
        const box = element[0].getBoundingClientRect();

        if ( box.right < window.innerWidth ) return
        const gap = box.right - window.innerWidth;

        if ( gap && gap < 5 ) return

        jQuery( element ).css( 'left', -gap - offset )
    }

    /**
     * Sets layer heights within a nav.
     * This is to make sure all the items are exposed...all the time!
     */
    object.setLayerHeights = function( nav, force = false ) {

		if ( window.innerWidth < object.config.hamburgerBreakpoint ) return

        // First, we need to get the active layer

        nav = jQuery( nav )
        const activeLayer = object.getActiveLayer( nav )

        // If there aren't any active layers, set all second-level layers to their implicit heights

        if ( ! activeLayer ) {

            const secondLevelLayers = nav.find( `${object.config.layerSelector}--second` );
            secondLevelLayers.each( ( index, secondLevelLayer ) => {
                secondLevelLayer = jQuery( secondLevelLayer )
                secondLevelLayer.css( 'height', force ? '' : object.getLayerImplicitHeight( secondLevelLayer ) )
            } )

            return false
        }

        // Then, we compare the heights of the first and last layers in the chain
        // If the last layer turns out to be taller, make them match

        const layerChain       = object.getLayerChain( activeLayer )
        const firstLayer       = layerChain.first()
        const lastLayer        = layerChain.last()
        const firstLayerHeight = firstLayer.outerHeight()
        const lastLayerHeight  = lastLayer.outerHeight()

        // Disabled enforced height causing issues with panel expension, when right panel is longer than left panel. Disabling seems fine, as it just expand the height needed.
        /*
        if ( ! firstLayer.is( lastLayer ) && lastLayerHeight > firstLayerHeight ) {
            firstLayer.css( 'height', firstLayerHeight )
            window.setTimeout( () => firstLayer.css( 'height', lastLayerHeight ), 0 )
        }
		else {
            firstLayer.css( 'height', object.getLayerImplicitHeight( firstLayer ) )
        }
        */

        return true
    }

    /** Helpers ------------------------- */

    /**
     * Gets the current active layer of the nav or given layer.
     * Essentially, for the nav, this is defined as the deepest layer that has the active class.
     */
    object.getActiveLayer = function( nav, currentLayer = null ) {

        nav = jQuery( nav )

        let layers      = []
        let activeLayer = false

        if ( currentLayer ) {
            currentLayer = jQuery( currentLayer) 
            layers       = currentLayer.find( object.config.layerSelector )
        }
		else {
            layers = nav.find( object.config.layerSelector )
        }

        layers.each( function( index, layer ) {

            layer = jQuery( layer )

            // If we find one, go recursive until we find another.
            // If we don't, that means this one is the deepest and therefore the active one.
            if ( layer.hasClass( object.config.isActiveClass ) ) {
                activeLayer = object.getActiveLayer( nav, layer ) || layer
            }
        } )

        return activeLayer
    }

    /**
     * Gets a chain of layers, starting at a specific one.
     */
    object.getLayerChain = function( layer ) {
        layer = jQuery( layer )
        const nav = layer.closest( object.config.selector )

        return layer.add( layer.parentsUntil( nav, object.config.layerSelector ) )
    }

    /**
     * Gets a layer's implicit height.
     * This is as if there is no height attribute applied to it.
     */
    object.getLayerImplicitHeight = function( layer ) {

        layer = jQuery( layer )
        const padding = parseFloat( layer.css( 'padding-top' ) ) + parseFloat( layer.css( 'padding-bottom' ) )

        const visibleChildren = layer.children( ':visible' )
        const firstChildRect  = visibleChildren.first().get( 0 ).getBoundingClientRect()
        const lastChildRect   = visibleChildren.last().get( 0 ).getBoundingClientRect()

        return padding + lastChildRect.bottom - firstChildRect.top
    }

	/**
	 * Focuses an element without shifting the dropdown.
	 * I have no idea why this is necessary, but this is just the world we live in.
	 */
	object.focusElement = function( targetElement ) {
		targetElement = jQuery( targetElement )
		var dropdown = targetElement.closest( object.config.dropdownSelector )

		if ( dropdown.length ) {
			window.setTimeout( function() {
				targetElement.focus()
			}, object.config.animationDuration )
		}
		else {
			targetElement.focus()
		}
	}

	/** Utility methods ----------------- */

	/**
     * Sets default accessibility attributes on traversable menu items.
     */
    object.setDefaultAttributes = function() {
        const links = jQuery( object.config.linkSelector )

        links.each( function( index, link ) {
            link = jQuery( link )
            const item = link.closest( object.config.itemSelector )

            if ( item.hasClass( object.config.hasChildrenClass ) ) {

                // Generate unique ID for the dropdown/submenu
                const uniqueId = `${object.config.namespace}-dropdown-${index}`

                // Find the associated dropdown or layer
                let target = link.siblings( object.config.dropdownSelector )
                if ( ! target.length ) {
                    target = link.siblings( object.config.layerSelector )
                }

                // Set ID on the target element
                if ( target.length ) {
                    target.attr( 'id', uniqueId )
                }

                // Set attributes on the link
                link.attr( 'role', 'button' )
                link.attr( 'aria-haspopup', 'true' )
                link.attr( 'aria-expanded', 'false' )
                link.attr( 'aria-controls', uniqueId )
            }
        } )
    }

    /* Initialization ------------------- */

	object.setDefaultAttributes()
    object.listen()
    return object
}

/** Export modules ------------------------------------------ */

export const traverseEventName = 'cuSupernavTraverse'

export default {

    init( config = {} ) {

		if ( ! config.namespace ) return

        let elements = document.querySelectorAll( `.js-${config.namespace}` )

        if ( ! elements.length || ! jQuery ) return

		const traversableClassName = `is-${config.namespace}-traversable`
		const depthAttribute = `${config.namespace}-depth`

        config = Object.assign(
            {
				
                // General config

				animationDuration  : 350,
				depthAttribute     : depthAttribute,
				hamburgerBreakpoint: 1024,
                hasChildrenClass   : `has-${config.namespace}-children`,
                traversesAttribute : `${config.namespace}-traverses`,

                // Selectors

                selector                      : `.js-${config.namespace}`,
				backButtonLinkSelector        : `.js-${config.namespace}__back-button-link`,
                breadcrumbAncestorLinkSelector: `.js-${config.namespace}__breadcrumb-ancestor-link`,
				contentLayerSelector          : `.js-${config.namespace}__submenu.${traversableClassName}[data-${depthAttribute}="0"]`,
                dropdownSelector              : `.js-${config.namespace}__dropdown`,
				eventDelegatorSelector        : 'body',
				itemSelector                  : `.js-${config.namespace}__menu-item.${traversableClassName}`,
                layerSelector                 : `.js-${config.namespace}__submenu.${traversableClassName}`,
                linkSelector                  : `.js-${config.namespace}__menu-item-link.${traversableClassName}`,
				menuWrapperSelector           : `.js-${config.namespace}__menu-wrapper.${traversableClassName}`,

                // State classes

                isActiveClass      : `is-${config.namespace}-active`,
                hasActiveChildClass: `has-${config.namespace}-active-child`,
            },
            config
        )

        return singleton( config )
    }
}